// The plan a seller picked on mytamilkart.com: /plan's Get started opens
// /signup?plan=<plan_id>. Saved here so it survives sign up, the OTP step, a
// switch to login and reloads, then /purchase-plan opens checkout for it once.
// Only the plan ID is carried: the price always comes from the API's order.
export const PENDING_PLAN_KEY = "pending_plan_id";

// A plan picked and then abandoned doesn't open checkout at a much later login
// (or for someone else on a shared computer). Logout clears it too
// (NavigationBar clears localStorage).
export const PENDING_PLAN_TTL_MS = 24 * 60 * 60 * 1000;

// Plan IDs are entered in Plan Management (e.g. "Starter", "YEARLY STARTER PLAN")
const isPlanId = (value) => typeof value === "string" && /^[A-Za-z0-9 _.-]{1,64}$/.test(value);

export const clearPendingPlan = () => {
    try { window.localStorage.removeItem(PENDING_PLAN_KEY); } catch (e) { /* storage blocked */ }
};

// Saved as { id, savedAt }; anything else, or older than the TTL, is dropped
export const getPendingPlan = (now = Date.now()) => {
    let saved;
    try {
        saved = JSON.parse(window.localStorage.getItem(PENDING_PLAN_KEY));
    } catch (e) {
        saved = null;
    }
    if (!saved) return null;
    const fresh = isPlanId(saved.id) && Number.isFinite(saved.savedAt)
        && saved.savedAt <= now && now - saved.savedAt < PENDING_PLAN_TTL_MS;
    if (!fresh) {
        clearPendingPlan();
        return null;
    }
    return saved.id;
};

// Saves a valid ?plan= from the URL; returns the pending plan (from the URL or saved earlier)
export const capturePendingPlan = (search = window.location.search, now = Date.now()) => {
    const id = new URLSearchParams(search).get("plan");
    if (isPlanId(id)) {
        try {
            window.localStorage.setItem(PENDING_PLAN_KEY, JSON.stringify({ id, savedAt: now }));
        } catch (e) { /* storage blocked: the URL still has it */ }
        return id;
    }
    return getPendingPlan(now);
};

export const purchasePlanPath = (id) => `/purchase-plan?plan=${encodeURIComponent(id)}`;

// Where to go after sign up or login: checkout for the pending plan, else `fallback`
export const afterLoginPath = (fallback) => {
    const id = getPendingPlan();
    return id ? purchasePlanPath(id) : fallback;
};

// A plan order whose Razorpay checkout was opened (issue #286). On mobile, a
// UPI payment takes the seller to the UPI app and the tab can be suspended or
// reloaded, so the checkout handler never verifies it: while this is set, the
// plans page asks the API (/plan/payment/reconcile) whether it was paid.
// Kept as long as the API looks back for such orders.
export const PENDING_PAYMENT_KEY = "pending_plan_payment";
export const PENDING_PAYMENT_TTL_MS = 2 * 24 * 60 * 60 * 1000;
// A check that finds the order unpaid this long after checkout opened stops
// checking (a cancelled or abandoned checkout); the Razorpay webhook still
// activates a payment that completes later
export const PENDING_PAYMENT_GIVE_UP_MS = 30 * 60 * 1000;

export const clearPendingPayment = () => {
    try { window.localStorage.removeItem(PENDING_PAYMENT_KEY); } catch (e) { /* storage blocked */ }
};

export const savePendingPayment = (orderId, now = Date.now()) => {
    try {
        window.localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({ order_id: String(orderId), savedAt: now }));
    } catch (e) { /* storage blocked: the webhook still activates the plan */ }
};

// How long ago checkout opened for the saved order, or null when there is none
export const pendingPaymentAge = (now = Date.now()) => {
    if (!getPendingPayment(now)) return null;
    try {
        return now - JSON.parse(window.localStorage.getItem(PENDING_PAYMENT_KEY)).savedAt;
    } catch (e) {
        return null;
    }
};

// The saved order ID, or null (none, malformed or older than the TTL)
export const getPendingPayment = (now = Date.now()) => {
    let saved;
    try {
        saved = JSON.parse(window.localStorage.getItem(PENDING_PAYMENT_KEY));
    } catch (e) {
        saved = null;
    }
    if (!saved) return null;
    const fresh = typeof saved.order_id === "string" && saved.order_id && Number.isFinite(saved.savedAt)
        && saved.savedAt <= now && now - saved.savedAt < PENDING_PAYMENT_TTL_MS;
    if (!fresh) {
        clearPendingPayment();
        return null;
    }
    return saved.order_id;
};
